import { Text } from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import { SearchUserEnriched } from 'lib-search-types';
import { User } from 'lib-shared-types-api';
import { ReactNode } from 'react';

import { AwayStatus } from 'apis/presence';
import styles from 'components/presence/components/presenceList.module.scss';
import {
  PresenceListItem,
  PresenceRowSelection,
} from 'components/presence/components/presenceListItem';
import { SidebarIconButton } from 'components/presence/components/sidebarIconButton';
import { CollapsedSections } from 'components/presence/hooks/useCollapsedSections';

type PresenceSectionProps = {
  /** Unique among the sections, to key its rows. */
  id: string;
  title: string;
  count: number;
  members?: SearchUserEnriched[];
  profile: User;
  awayStatuses: AwayStatus[];
  selection: PresenceRowSelection;
  /** Rendered at the end of the header row. */
  actions?: ReactNode;
  /** Nested `PresenceSection`s, indented one level below this one. */
  children?: ReactNode;
  collapsedSections?: CollapsedSections;
  renderRowAction?: (member: SearchUserEnriched) => ReactNode;
};

export const PresenceSection = ({
  id,
  title,
  count,
  members = [],
  profile,
  awayStatuses,
  selection,
  actions,
  children,
  collapsedSections,
  renderRowAction,
}: PresenceSectionProps): JSX.Element => {
  const isCollapsed = collapsedSections?.isCollapsed(id) ?? false;
  const toggle = () => collapsedSections?.toggle(id);

  const heading = (
    <Text.Paragraph
      as={collapsedSections ? 'span' : 'p'}
      variant="3"
      color="var(--sidebar-text-color-secondary)"
    >
      {title} ({count})
    </Text.Paragraph>
  );

  return (
    <li>
      <div
        className={classnames(styles.groupHead, {
          [styles.groupHeadWithActions]: actions || collapsedSections,
        })}
      >
        {collapsedSections ? (
          <button
            type="button"
            className={styles.groupToggle}
            aria-expanded={!isCollapsed}
            onClick={toggle}
          >
            {heading}
          </button>
        ) : (
          heading
        )}
        {(actions || collapsedSections) && (
          <div className={styles.groupActions}>
            {actions}
            {collapsedSections && (
              <SidebarIconButton
                icon="angle-down"
                label={isCollapsed ? 'Expand' : 'Collapse'}
                hideTooltip
                tabIndex={-1}
                className={classnames(styles.groupChevron, {
                  [styles.groupChevronCollapsed]: isCollapsed,
                })}
                onClick={toggle}
              />
            )}
          </div>
        )}
      </div>
      {!isCollapsed && members.length > 0 && (
        <ul className={styles.list}>
          {members.map((member) => (
            <PresenceListItem
              key={member.id}
              sectionId={id}
              member={member}
              ownProfile={profile}
              awayStatuses={awayStatuses}
              selection={selection}
              action={renderRowAction?.(member)}
            />
          ))}
        </ul>
      )}
      {!isCollapsed && children && (
        <ul className={classnames(styles.list, styles.nested)}>{children}</ul>
      )}
    </li>
  );
};
